import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { taskKeys } from './useTasks';

// Types
type DependencyType = 'FS' | 'SS' | 'FF' | 'SF';

interface TaskDependency {
  id: string;
  predecessorId: string;
  successorId: string;
  type: DependencyType;
  lag: number;
  lagUnit: string;
  predecessorTask?: {
    id: string;
    rowNumber: number;
    name: string;
  };
  successorTask?: {
    id: string;
    rowNumber: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

function normalizeDependency(payload: any): TaskDependency {
  const rawType = payload?.type ?? payload?.dependencyType ?? 'fs';
  const normalizedType = String(rawType).toUpperCase() as DependencyType;

  return {
    id: payload.id,
    predecessorId: payload.predecessorId,
    successorId: payload.successorId,
    type: (['FS', 'SS', 'FF', 'SF'].includes(normalizedType)
      ? normalizedType
      : 'FS') as DependencyType,
    lag: payload.lag ?? payload.lagDays ?? 0,
    lagUnit: payload.lagUnit ?? 'd',
    predecessorTask: payload.predecessorTask ?? payload.predecessor,
    successorTask: payload.successorTask ?? payload.successor,
    createdAt: payload.createdAt,
    updatedAt: payload.updatedAt,
  };
}

interface CreateDependencyDto {
  predecessorId: string;
  successorId: string;
  type?: DependencyType;
  lag?: number;
  lagUnit?: string;
}

interface UpdateDependencyDto {
  type?: DependencyType;
  lag?: number;
  lagUnit?: string;
}

// Query keys
export const dependencyKeys = {
  all: ['dependencies'] as const,
  lists: () => [...dependencyKeys.all, 'list'] as const,
  byTask: (taskId: string) => [...dependencyKeys.lists(), 'task', taskId] as const,
  bySheet: (sheetId: string) => [...dependencyKeys.lists(), 'sheet', sheetId] as const,
  detail: (dependencyId: string) => [...dependencyKeys.all, 'detail', dependencyId] as const,
};

// Fetch dependencies for a task (both predecessors and successors)
export function useDependenciesQuery(taskId: string) {
  return useQuery({
    queryKey: dependencyKeys.byTask(taskId),
    queryFn: async () => {
      const response = await api.get<{
        predecessors: TaskDependency[];
        successors: TaskDependency[];
      }>(`/tasks/${taskId}/dependencies`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 30000,
  });
}

// Fetch all dependencies for a sheet (for Gantt chart visualization)
export function useSheetDependenciesQuery(sheetId: string) {
  return useQuery({
    queryKey: dependencyKeys.bySheet(sheetId),
    queryFn: async () => {
      const response = await api.get<any[]>(
        `/sheets/${sheetId}/dependencies`
      );
      if (!Array.isArray(response.data)) return [];
      return response.data.map(normalizeDependency);
    },
    enabled: !!sheetId,
    staleTime: 30000,
  });
}

// Create dependency mutation
export function useCreateDependency(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateDependencyDto) => {
      const response = await api.post<TaskDependency>('/dependencies', dto);
      return response.data;
    },
    onMutate: async (newDependency) => {
      // Cancel related queries
      await queryClient.cancelQueries({
        queryKey: dependencyKeys.byTask(newDependency.successorId),
      });
      await queryClient.cancelQueries({
        queryKey: dependencyKeys.byTask(newDependency.predecessorId),
      });
      await queryClient.cancelQueries({
        queryKey: dependencyKeys.bySheet(sheetId),
      });

      // Get previous data for rollback
      const previousSuccessorDeps = queryClient.getQueryData<{
        predecessors: TaskDependency[];
        successors: TaskDependency[];
      }>(dependencyKeys.byTask(newDependency.successorId));

      const previousPredecessorDeps = queryClient.getQueryData<{
        predecessors: TaskDependency[];
        successors: TaskDependency[];
      }>(dependencyKeys.byTask(newDependency.predecessorId));

      const previousSheetDeps = queryClient.getQueryData<TaskDependency[]>(
        dependencyKeys.bySheet(sheetId)
      );

      // Create optimistic dependency
      const optimisticDep: TaskDependency = {
        id: `temp-${Date.now()}`,
        predecessorId: newDependency.predecessorId,
        successorId: newDependency.successorId,
        type: newDependency.type || 'FS',
        lag: newDependency.lag || 0,
        lagUnit: newDependency.lagUnit || 'd',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update successor's dependencies
      if (previousSuccessorDeps) {
        queryClient.setQueryData(
          dependencyKeys.byTask(newDependency.successorId),
          {
            ...previousSuccessorDeps,
            predecessors: [...previousSuccessorDeps.predecessors, optimisticDep],
          }
        );
      }

      // Optimistically update predecessor's dependencies
      if (previousPredecessorDeps) {
        queryClient.setQueryData(
          dependencyKeys.byTask(newDependency.predecessorId),
          {
            ...previousPredecessorDeps,
            successors: [...previousPredecessorDeps.successors, optimisticDep],
          }
        );
      }

      // Optimistically update sheet dependencies
      if (previousSheetDeps) {
        queryClient.setQueryData(dependencyKeys.bySheet(sheetId), [
          ...previousSheetDeps,
          optimisticDep,
        ]);
      }

      return {
        previousSuccessorDeps,
        previousPredecessorDeps,
        previousSheetDeps,
        newDependency,
      };
    },
    onError: (_err, _newDep, context) => {
      // Rollback on error
      if (context?.previousSuccessorDeps) {
        queryClient.setQueryData(
          dependencyKeys.byTask(context.newDependency.successorId),
          context.previousSuccessorDeps
        );
      }
      if (context?.previousPredecessorDeps) {
        queryClient.setQueryData(
          dependencyKeys.byTask(context.newDependency.predecessorId),
          context.previousPredecessorDeps
        );
      }
      if (context?.previousSheetDeps) {
        queryClient.setQueryData(
          dependencyKeys.bySheet(sheetId),
          context.previousSheetDeps
        );
      }
    },
    onSettled: (_data, _err, newDep) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: dependencyKeys.byTask(newDep.successorId),
      });
      queryClient.invalidateQueries({
        queryKey: dependencyKeys.byTask(newDep.predecessorId),
      });
      queryClient.invalidateQueries({
        queryKey: dependencyKeys.bySheet(sheetId),
      });
      // Also invalidate task queries as dependencies affect scheduling
      queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
    },
  });
}

// Update dependency mutation
export function useUpdateDependency(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dependencyId,
      updates,
    }: {
      dependencyId: string;
      updates: UpdateDependencyDto;
    }) => {
      const response = await api.patch<TaskDependency>(
        `/dependencies/${dependencyId}`,
        updates
      );
      return response.data;
    },
    onMutate: async ({ dependencyId, updates }) => {
      await queryClient.cancelQueries({
        queryKey: dependencyKeys.bySheet(sheetId),
      });

      const previousSheetDeps = queryClient.getQueryData<TaskDependency[]>(
        dependencyKeys.bySheet(sheetId)
      );

      if (previousSheetDeps) {
        queryClient.setQueryData<TaskDependency[]>(
          dependencyKeys.bySheet(sheetId),
          previousSheetDeps.map((dep) =>
            dep.id === dependencyId
              ? { ...dep, ...updates, updatedAt: new Date().toISOString() }
              : dep
          )
        );
      }

      return { previousSheetDeps };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousSheetDeps) {
        queryClient.setQueryData(
          dependencyKeys.bySheet(sheetId),
          context.previousSheetDeps
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: dependencyKeys.bySheet(sheetId),
      });
      queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
    },
  });
}

// Delete dependency mutation
export function useDeleteDependency(sheetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dependencyId: string) => {
      await api.delete(`/dependencies/${dependencyId}`);
      return dependencyId;
    },
    onMutate: async (dependencyId) => {
      await queryClient.cancelQueries({
        queryKey: dependencyKeys.bySheet(sheetId),
      });

      const previousSheetDeps = queryClient.getQueryData<TaskDependency[]>(
        dependencyKeys.bySheet(sheetId)
      );

      // Find the dependency to get task IDs for cache invalidation
      const deletedDep = previousSheetDeps?.find((d) => d.id === dependencyId);

      if (previousSheetDeps) {
        queryClient.setQueryData<TaskDependency[]>(
          dependencyKeys.bySheet(sheetId),
          previousSheetDeps.filter((dep) => dep.id !== dependencyId)
        );
      }

      // Also update task-specific caches if we have the dependency info
      if (deletedDep) {
        const successorDeps = queryClient.getQueryData<{
          predecessors: TaskDependency[];
          successors: TaskDependency[];
        }>(dependencyKeys.byTask(deletedDep.successorId));

        const predecessorDeps = queryClient.getQueryData<{
          predecessors: TaskDependency[];
          successors: TaskDependency[];
        }>(dependencyKeys.byTask(deletedDep.predecessorId));

        if (successorDeps) {
          queryClient.setQueryData(
            dependencyKeys.byTask(deletedDep.successorId),
            {
              ...successorDeps,
              predecessors: successorDeps.predecessors.filter(
                (d) => d.id !== dependencyId
              ),
            }
          );
        }

        if (predecessorDeps) {
          queryClient.setQueryData(
            dependencyKeys.byTask(deletedDep.predecessorId),
            {
              ...predecessorDeps,
              successors: predecessorDeps.successors.filter(
                (d) => d.id !== dependencyId
              ),
            }
          );
        }
      }

      return { previousSheetDeps, deletedDep };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousSheetDeps) {
        queryClient.setQueryData(
          dependencyKeys.bySheet(sheetId),
          context.previousSheetDeps
        );
      }
    },
    onSettled: (_data, _err, _depId, context) => {
      queryClient.invalidateQueries({
        queryKey: dependencyKeys.bySheet(sheetId),
      });
      if (context?.deletedDep) {
        queryClient.invalidateQueries({
          queryKey: dependencyKeys.byTask(context.deletedDep.successorId),
        });
        queryClient.invalidateQueries({
          queryKey: dependencyKeys.byTask(context.deletedDep.predecessorId),
        });
      }
      queryClient.invalidateQueries({ queryKey: taskKeys.list(sheetId) });
    },
  });
}

export type { TaskDependency, CreateDependencyDto, UpdateDependencyDto, DependencyType };
